import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaInbox, FaPaperPlane, FaClock, FaTrash, FaEnvelope, FaEnvelopeOpen,
  FaPlus, FaTimes, FaCalendarAlt, FaFolder, FaSearch, FaReply,
  FaStar, FaArchive, FaSpinner, FaCheck
} from 'react-icons/fa';
import api from '../utils/api';

const MessagingPage = () => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'inbox') {
        const [inboxRes, foldersRes] = await Promise.all([
          api.get('/messaging/inbox'),
          api.get('/messaging/folders')
        ]);
        setEmails(inboxRes.data.emails);
        setFolders(foldersRes.data.folders);
      } else if (activeTab === 'sent') {
        const res = await api.get('/messaging/sent');
        setSentEmails(res.data.emails);
      } else if (activeTab === 'scheduled') {
        const res = await api.get('/messaging/scheduled');
        setScheduledEmails(res.data.emails);
      }
    } catch (error) {
      console.error('Error loading emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmail = async (emailId) => {
    if (!window.confirm('Supprimer cet email ?')) return;
    try {
      await api.delete(`/messaging/${emailId}`);
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleCancelScheduled = async (emailId) => {
    if (!window.confirm('Annuler cet envoi programmé ?')) return;
    try {
      await api.delete(`/messaging/scheduled/${emailId}`);
      loadData();
    } catch (error) {
      console.error('Error canceling:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Hier';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'long' });
    } else {
      return date.toLocaleDateString('fr-FR');
    }
  };

  const filteredEmails = (activeTab === 'inbox' ? emails : activeTab === 'sent' ? sentEmails : scheduledEmails)
    .filter(e => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        e.subject?.toLowerCase().includes(term) ||
        e.sender?.toLowerCase().includes(term) ||
        (e.toEmails || []).join(' ').toLowerCase().includes(term)
      );
    });

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0">
          <button
            onClick={() => setShowCompose(true)}
            className="w-full mb-6 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 flex items-center justify-center gap-2 shadow-lg"
          >
            <FaPlus /> Nouveau message
          </button>
          
          <nav className="space-y-1">
            {[
              { id: 'inbox', icon: FaInbox, label: 'Boîte de réception', count: emails.length },
              { id: 'sent', icon: FaPaperPlane, label: 'Envoyés', count: sentEmails.length },
              { id: 'scheduled', icon: FaClock, label: 'Programmés', count: scheduledEmails.filter(e => e.status === 'pending').length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full px-4 py-3 rounded-lg flex items-center gap-3 transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-blue-100 text-blue-700 font-bold' 
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <tab.icon />
                <span className="flex-1 text-left">{tab.label}</span>
                {tab.count > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-gray-200'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
          
          {/* Dossiers */}
          {folders.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 px-4">
                Dossiers
              </h3>
              {folders.slice(0, 5).map(folder => (
                <button
                  key={folder}
                  className="w-full px-4 py-2 rounded-lg flex items-center gap-3 hover:bg-gray-100 text-gray-600 text-sm"
                >
                  <FaFolder className="text-yellow-500" />
                  {folder}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 bg-white rounded-xl border shadow-sm overflow-hidden">
          {/* Search bar */}
          <div className="p-4 border-b">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher dans les emails..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Email list */}
          <div className="divide-y max-h-[calc(100vh-300px)] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <FaSpinner className="animate-spin text-3xl text-blue-600" />
              </div>
            ) : filteredEmails.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FaEnvelope className="text-5xl mx-auto mb-4 opacity-50" />
                <p>Aucun email</p>
              </div>
            ) : (
              filteredEmails.map(email => (
                <motion.div
                  key={email.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`p-4 hover:bg-gray-50 cursor-pointer flex items-start gap-4 ${
                    !email.isRead ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => setSelectedEmail(email)}
                >
                  <div className="flex-shrink-0">
                    {email.isRead ? (
                      <FaEnvelopeOpen className="text-gray-400" />
                    ) : (
                      <FaEnvelope className="text-blue-600" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-bold truncate ${!email.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                        {activeTab === 'sent' || activeTab === 'scheduled' 
                          ? (email.toEmails || []).join(', ')
                          : email.sender
                        }
                      </span>
                      {activeTab === 'scheduled' && (
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          email.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          email.status === 'sent' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {email.status === 'pending' ? 'En attente' : 
                           email.status === 'sent' ? 'Envoyé' : 'Échoué'}
                        </span>
                      )}
                    </div>
                    <p className={`truncate ${!email.isRead ? 'font-bold' : ''}`}>
                      {email.subject}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {email.bodyText?.substring(0, 100) || email.htmlContent?.substring(0, 100)}...
                    </p>
                  </div>
                  
                  <div className="flex-shrink-0 text-right">
                    <p className="text-sm text-gray-500">
                      {formatDate(email.date || email.scheduledAt || email.sentAt)}
                    </p>
                    {activeTab === 'scheduled' && email.status === 'pending' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelScheduled(email.id);
                        }}
                        className="mt-2 text-red-600 hover:text-red-700 text-sm"
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Email detail modal */}
      <AnimatePresence>
        {selectedEmail && (
          <EmailDetailModal
            email={selectedEmail}
            onClose={() => setSelectedEmail(null)}
            onDelete={() => {
              handleDeleteEmail(selectedEmail.id);
              setSelectedEmail(null);
            }}
            onReply={() => {
              setShowCompose(true);
              // Pre-fill reply
            }}
          />
        )}
      </AnimatePresence>

      {/* Compose modal */}
      <AnimatePresence>
        {showCompose && (
          <ComposeModal
            onClose={() => setShowCompose(false)}
            onSent={() => {
              loadData();
              setShowCompose(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Modal détail email
const EmailDetailModal = ({ email, onClose, onDelete, onReply }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold truncate flex-1">{email.subject}</h2>
          <div className="flex items-center gap-2">
            <button onClick={onReply} className="p-2 hover:bg-gray-100 rounded" title="Répondre">
              <FaReply />
            </button>
            <button onClick={onDelete} className="p-2 hover:bg-red-100 text-red-600 rounded" title="Supprimer">
              <FaTrash />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
              <FaTimes />
            </button>
          </div>
        </div>
        
        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
              {email.sender?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-bold">{email.sender}</p>
              <p className="text-sm text-gray-500">
                À: {(email.recipients || email.toEmails || []).join(', ')}
              </p>
              <p className="text-sm text-gray-500">
                {new Date(email.date || email.sentAt).toLocaleString('fr-FR')}
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {email.bodyHtml || email.htmlContent ? (
            <div dangerouslySetInnerHTML={{ __html: email.bodyHtml || email.htmlContent }} />
          ) : (
            <pre className="whitespace-pre-wrap font-sans">{email.bodyText}</pre>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Modal composition
const ComposeModal = ({ onClose, onSent, replyTo = null }) => {
  const [formData, setFormData] = useState({
    toEmails: replyTo ? [replyTo.sender] : [''],
    subject: replyTo ? `Re: ${replyTo.subject}` : '',
    body: '',
    scheduledAt: ''
  });
  const [isScheduled, setIsScheduled] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!formData.toEmails[0] || !formData.subject || !formData.body) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    setSending(true);
    try {
      if (isScheduled && formData.scheduledAt) {
        await api.post('/messaging/schedule', {
          toEmails: formData.toEmails.filter(e => e.trim()),
          subject: formData.subject,
          htmlContent: `<div style="font-family: Arial, sans-serif;">${formData.body.replace(/\n/g, '<br>')}</div>`,
          scheduledAt: formData.scheduledAt
        });
        alert('Email programmé !');
      } else {
        await api.post('/messaging/send', {
          toEmails: formData.toEmails.filter(e => e.trim()),
          subject: formData.subject,
          htmlContent: `<div style="font-family: Arial, sans-serif;">${formData.body.replace(/\n/g, '<br>')}</div>`
        });
        alert('Email envoyé !');
      }
      onSent();
    } catch (error) {
      console.error('Error:', error);
      alert('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4"
    >
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="bg-white rounded-t-xl sm:rounded-xl w-full sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
      >
        <div className="p-4 border-b flex items-center justify-between bg-gray-50">
          <h2 className="text-lg font-bold">Nouveau message</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded">
            <FaTimes />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Destinataires */}
          <div>
            <label className="block text-sm font-bold mb-1">À</label>
            <input
              type="email"
              value={formData.toEmails[0]}
              onChange={(e) => setFormData({ ...formData, toEmails: [e.target.value] })}
              placeholder="destinataire@email.com"
              className="w-full p-3 border rounded-lg focus:border-blue-500 focus:outline-none"
            />
          </div>
          
          {/* Sujet */}
          <div>
            <label className="block text-sm font-bold mb-1">Sujet</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Sujet du message"
              className="w-full p-3 border rounded-lg focus:border-blue-500 focus:outline-none"
            />
          </div>
          
          {/* Corps */}
          <div>
            <label className="block text-sm font-bold mb-1">Message</label>
            <textarea
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              placeholder="Écrivez votre message..."
              rows={10}
              className="w-full p-3 border rounded-lg focus:border-blue-500 focus:outline-none resize-none"
            />
          </div>
          
          {/* Programmation */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isScheduled}
                onChange={(e) => setIsScheduled(e.target.checked)}
                className="w-5 h-5"
              />
              <FaClock className="text-gray-500" />
              <span>Programmer l'envoi</span>
            </label>
            
            {isScheduled && (
              <input
                type="datetime-local"
                value={formData.scheduledAt}
                onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                className="flex-1 p-2 border rounded-lg"
              />
            )}
          </div>
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 border rounded-lg font-bold hover:bg-gray-100"
          >
            Annuler
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {sending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
            {isScheduled ? 'Programmer' : 'Envoyer'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MessagingPage;
